import type { CashbackConfig, CashbackSummary } from '@erp/shared';
import { BadgeCheck, Coins, Gift, TrendingDown, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button, Card, FormField, Input, Skeleton, StatCard } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { useAuthStore } from '../../lib/authStore.js';
import {
  ensureCashbackAccounts,
  expireCashback,
  getCashbackConfig,
  getCashbackSummary,
  setCashbackConfig,
} from '../../lib/cashback.js';
import { formatDecimals } from '../../lib/money.js';

export function CashbackSettingsPage(): JSX.Element {
  const { t, i18n } = useTranslation('cashback');
  const { t: tc } = useTranslation();
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  // Cashback KPIs are base-currency money — render with that currency's decimals.
  const fmt = (n: string | number): string => formatDecimals(n, baseCurrency, i18n.language);
  const canWrite = usePermission('cashback.write');

  const [cfg, setCfg] = useState<CashbackConfig | null>(null);
  const [summary, setSummary] = useState<CashbackSummary | null>(null);
  const [saving, setSaving] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [expiring, setExpiring] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [c, s] = await Promise.all([getCashbackConfig(), getCashbackSummary()]);
        setCfg(c);
        setSummary(s);
      } catch {
        toast.error(tc('errors:server'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (p: Partial<CashbackConfig>): void => setCfg((c) => (c ? { ...c, ...p } : c));

  const onSave = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!cfg) return;
    setSaving(true);
    try {
      const saved = await setCashbackConfig({
        earnRatePct: cfg.earnRatePct,
        minRedeem: cfg.minRedeem,
        maxRedeemPct: cfg.maxRedeemPct,
        channels: cfg.channels,
        expiryMonths: cfg.expiryMonths,
        postToGl: cfg.postToGl,
        registrationBonus: cfg.registrationBonus || '0',
        normalEarnsCashback: cfg.normalEarnsCashback ?? true,
        normalGetsRegistrationBonus: cfg.normalGetsRegistrationBonus ?? true,
      });
      setCfg(saved);
      toast.success(t('saved', { defaultValue: 'Cashback settings saved.' }));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  const onSetupAccounts = async (): Promise<void> => {
    setEnsuring(true);
    try {
      setCfg(await ensureCashbackAccounts());
      toast.success(t('accountsReady', { defaultValue: 'Cashback GL accounts are ready.' }));
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setEnsuring(false);
    }
  };

  if (!cfg || !summary) {
    return (
      <Card>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-40 w-full" />
      </Card>
    );
  }

  const accountsReady = Boolean(cfg.liabilityAccountId && cfg.expenseAccountId);

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('title', { defaultValue: 'Cashback' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('subtitle', { defaultValue: 'Reward customers with money-valued cashback they can spend on future purchases.' })}
        </p>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label={t('kpi.liability', { defaultValue: 'Outstanding cashback' })} value={fmt(summary.outstandingLiability)} icon={Coins} tone="info" />
        <StatCard label={t('kpi.earned', { defaultValue: 'Lifetime earned' })} value={fmt(summary.lifetimeEarned)} icon={Gift} tone="success" />
        <StatCard label={t('kpi.redeemed', { defaultValue: 'Lifetime redeemed' })} value={fmt(summary.lifetimeRedeemed)} icon={TrendingDown} />
        <StatCard label={t('kpi.customers', { defaultValue: 'Customers with cashback' })} value={String(summary.customers)} icon={Users} />
      </div>

      <Card className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <BadgeCheck className={accountsReady ? 'h-5 w-5 text-success' : 'h-5 w-5 text-fg-muted'} aria-hidden />
          <div>
            <div className="text-sm font-semibold text-fg">{t('accounts.title', { defaultValue: 'Ledger accounts' })}</div>
            <div className="text-xs text-fg-muted">
              {accountsReady
                ? t('accounts.ready', { defaultValue: 'Cashback liability + expense accounts are set up.' })
                : t('accounts.missing', { defaultValue: 'Create the cashback liability + expense accounts to post to the ledger.' })}
            </div>
          </div>
        </div>
        {canWrite && !accountsReady && (
          <Button variant="secondary" onClick={() => void onSetupAccounts()} loading={ensuring}>
            {t('accounts.setup', { defaultValue: 'Set up accounts' })}
          </Button>
        )}
      </Card>

      <Card>
        <h2 className="mb-4 text-lg font-semibold">{t('config.title', { defaultValue: 'Program settings' })}</h2>
        <form onSubmit={onSave} className="flex flex-col gap-4" noValidate>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField id="cb-earn" label={t('config.earnRate', { defaultValue: 'Earn rate (% of eligible subtotal)' })}>
              <Input id="cb-earn" type="number" min={0} max={100} step={0.5} value={cfg.earnRatePct}
                onChange={(e) => patch({ earnRatePct: Number(e.target.value) })} disabled={!canWrite} />
            </FormField>
            <FormField id="cb-maxredeem" label={t('config.maxRedeem', { defaultValue: 'Max paid by cashback (%)' })}>
              <Input id="cb-maxredeem" type="number" min={0} max={100} step={1} value={cfg.maxRedeemPct}
                onChange={(e) => patch({ maxRedeemPct: Number(e.target.value) })} disabled={!canWrite} />
            </FormField>
            <FormField id="cb-minredeem" label={t('config.minRedeem', { defaultValue: 'Minimum balance to redeem' })}>
              <Input id="cb-minredeem" inputMode="decimal" value={cfg.minRedeem}
                onChange={(e) => patch({ minRedeem: e.target.value })} disabled={!canWrite} />
            </FormField>
            <FormField
              id="cb-regbonus"
              label={t('config.registrationBonus', { defaultValue: 'Registration cashback', currency: baseCurrency })}
              helperText={t('config.registrationBonusHint', { defaultValue: 'Credited once when a customer registers on the app or store. 0 turns it off.', currency: baseCurrency })}
            >
              <Input id="cb-regbonus" inputMode="decimal" value={cfg.registrationBonus ?? '0'}
                onChange={(e) => patch({ registrationBonus: e.target.value })} disabled={!canWrite} />
            </FormField>
          </div>

          {/* Cashback for customers with NO group. Every other group is switched on
              its own row under Price lists, so this page holds only the rule that
              has nowhere else to live — and says where the rest of it is, because
              a setting that is silently somewhere else reads as a missing one. */}
          <FormField
            id="cb-normal"
            label={t('config.normalCustomers', { defaultValue: 'Normal customers (no customer type)' })}
          >
            <div className="flex flex-col gap-1.5">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={cfg.normalEarnsCashback ?? true}
                  disabled={!canWrite}
                  onChange={(e) => patch({ normalEarnsCashback: e.target.checked })}
                />
                <span>{t('config.normalEarns', { defaultValue: 'Earn cashback on purchases' })}</span>
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={cfg.normalGetsRegistrationBonus ?? true}
                  disabled={!canWrite}
                  onChange={(e) => patch({ normalGetsRegistrationBonus: e.target.checked })}
                />
                <span>{t('config.normalRegistration', { defaultValue: 'Get the registration cashback' })}</span>
              </label>
              <p className="mt-1 text-sm text-fg-muted">
                {t('config.groupsElsewhere', {
                  defaultValue: 'For every other customer type, set it on the group itself:',
                })}{' '}
                <Link to="/sales/price-lists" className="text-brand underline-offset-4 hover:underline">
                  {t('config.goToPriceLists', { defaultValue: 'Price lists' })}
                </Link>
              </p>
            </div>
          </FormField>

          <FormField id="cb-expiry" label={t('config.expiry', { defaultValue: 'Cashback expires after (months) — blank = never' })}>
            <div className="flex items-center gap-2">
              <Input id="cb-expiry" type="number" min={1} max={120} value={cfg.expiryMonths ?? ''}
                onChange={(e) => patch({ expiryMonths: e.target.value ? Number(e.target.value) : null })} disabled={!canWrite} />
              {canWrite && cfg.expiryMonths != null && (
                <Button type="button" variant="secondary" loading={expiring} onClick={async () => {
                  setExpiring(true);
                  try {
                    const r = await expireCashback();
                    toast.success(t('expire.done', { count: r.partiesExpired, defaultValue: `Expired cashback for ${r.partiesExpired} customer(s).` }));
                    setSummary(await getCashbackSummary());
                  } catch { toast.error(tc('errors:server')); } finally { setExpiring(false); }
                }}>
                  {t('expire.run', { defaultValue: 'Run expiry now' })}
                </Button>
              )}
            </div>
          </FormField>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium text-fg">{t('config.channels', { defaultValue: 'Where cashback applies' })}</legend>
            {(['pos', 'invoice', 'online'] as const).map((ch) => (
              <label key={ch} className="flex items-center gap-2 text-sm text-fg">
                <input type="checkbox" className="h-4 w-4 accent-brand" checked={cfg.channels[ch]} disabled={!canWrite}
                  onChange={(e) => patch({ channels: { ...cfg.channels, [ch]: e.target.checked } })} />
                {t(`config.channel.${ch}`, { defaultValue: ch })}
              </label>
            ))}
          </fieldset>

          <label className="flex items-center gap-2 text-sm text-fg">
            <input type="checkbox" className="h-4 w-4 accent-brand" checked={cfg.postToGl} disabled={!canWrite}
              onChange={(e) => patch({ postToGl: e.target.checked })} />
            {t('config.postToGl', { defaultValue: 'Post earn/redeem to the General Ledger (loyalty liability)' })}
          </label>

          {canWrite && (
            <div>
              <Button type="submit" loading={saving} disabled={cfg.postToGl && !accountsReady}>
                {tc('actions.save')}
              </Button>
              {cfg.postToGl && !accountsReady && (
                <span className="ms-3 text-xs text-danger">{t('config.needAccounts', { defaultValue: 'Set up the ledger accounts first.' })}</span>
              )}
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}
